import { IReleaseDate } from '@/types/IRelease'
import dayjs from 'dayjs'
import { useUserProfile } from 'hooks/useUserProfile'
import useTranslation from 'next-translate/useTranslation'
import { useCallback, useEffect, useState } from 'react'
import { getExactReleaseDate } from 'utils/release'

export const useReleaseInfo = (
  releaseDate?: IReleaseDate,
  raffle?: boolean
) => {
  const { t } = useTranslation('release_calendar')
  const { data: userProfile } = useUserProfile()

  const getReleaseInfo = useCallback(() => {
    if (!releaseDate) return {}

    const timezone = userProfile?.releaseSettings?.timeZone || dayjs.tz.guess()
    const exactReleaseDates = getExactReleaseDate(releaseDate, timezone, raffle)

    if (!exactReleaseDates?.releaseEnd && !exactReleaseDates?.releaseStart)
      return {}

    const { releaseStart, releaseEnd } = exactReleaseDates

    const releaseStartDate = dayjs(releaseStart).tz(timezone)
    const current = dayjs().tz(timezone)

    const daysDiff = releaseStartDate.diff(current, 'days')
    const hoursDiff = releaseStartDate.diff(current, 'hours')
    const minutesDiff = releaseStartDate.diff(current, 'minutes')
    const secondsDiff = releaseStartDate.diff(current, 'seconds')

    if (releaseDate.dateOnly && releaseStartDate) {
      if (current.isBefore(releaseStartDate, 'date'))
        return { releaseText: t('time_tbd'), canSaveRelease: false }

      if (releaseStartDate.isSame(current, 'date'))
        return { releaseText: t('contact_gallery'), canSaveRelease: false }
    }

    if (releaseStart && releaseEnd) {
      const end = dayjs(releaseEnd).tz(timezone)

      if (
        current.isAfter(releaseStartDate, 'milliseconds') &&
        !current.isAfter(end, 'milliseconds')
      )
        return { releaseText: t('common:live'), canSaveRelease: false }
    }

    if (
      daysDiff === 0 &&
      hoursDiff <= 0 &&
      minutesDiff <= 0 &&
      secondsDiff <= 0
    ) {
      return { releaseText: t('common:released'), canSaveRelease: false }
    }

    const hoursRest = hoursDiff % 24
    const minutesRest = minutesDiff % 60

    if (hoursDiff >= 0 && hoursDiff < 24) {
      const secondsRest = secondsDiff % 60

      return {
        releaseText: t('common:x_hours_y_mins_z_seconds', {
          x: hoursDiff,
          y: minutesRest,
          z: secondsRest,
        }),
        canSaveRelease: true,
      }
    }

    if (daysDiff < 0) {
      return {
        releaseText: t(
          `common:x_days_ago_${daysDiff === -1 ? 'one' : 'other'}`,
          {
            x: Math.abs(daysDiff),
          }
        ),
        canSaveRelease: false,
      }
    }

    if (daysDiff >= 1 && daysDiff < 7) {
      return {
        releaseText: t('common:x_days_y_hours_z_mins', {
          x: daysDiff,
          y: hoursRest,
          z: minutesRest,
        }),
        canSaveRelease: true,
      }
    }

    if (daysDiff >= 7) {
      return {
        releaseText: t('common:x_days_y_hours', { x: daysDiff, y: hoursRest }),
        canSaveRelease: true,
      }
    }

    return {
      releaseText: t(`common:x_days_${daysDiff === 1 ? 'one' : 'other'}`, {
        x: daysDiff,
      }),
      canSaveRelease: true,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [releaseDate, userProfile?.releaseSettings?.timeZone])

  const [releaseInfo, setReleaseInfo] = useState(getReleaseInfo)

  useEffect(() => {
    const interval = setInterval(() => setReleaseInfo(getReleaseInfo), 1000)

    return () => {
      clearInterval(interval)
    }
  }, [getReleaseInfo])

  return releaseInfo
}
